/**
 * Compensation item router (US-029).
 *
 * Lets athletes manage the structured `CompensationItem` rows on their
 * own deals. Mirrors the pattern of `payor.router.ts`: scoped to the
 * signed-in athlete, transactional updates, server-side recalculation
 * of the cached `Deal.amount` so existing dashboards keep working.
 *
 * Read access for items is through `deal.get` (the deal handler includes
 * `compensationItems` in its select). This router is write-only +
 * `reorderItems`.
 *
 * Deferred to follow-ups (out of scope for the first ship):
 *   - Drag-and-drop UI for reorder (orderIndex is server-supported; UI
 *     ships with up/down buttons for v1).
 *   - Per-item due-date reminders (US-005 handles deal-level reminders;
 *     bonus-milestone reminders will land with US-033 readiness check).
 *
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import {
  compensationItemFieldsSchema,
  compensationItemSchema,
  compensationTypeEnum,
} from '@app/validators';
import { z } from 'zod';
import { protectedProcedure } from '../middleware/auth';

// Used by both add and update — the validator's superRefine handles the
// cross-field rules (royalty needs %, volume low ≤ high).
const addItemInput = z.object({
  dealId: z.string(),
  item: compensationItemSchema,
});

const updateItemInput = z.object({
  id: z.string(),
  // Use the raw-fields schema (no .superRefine), since zod 4 forbids
  // .partial() on a refined schema. Cross-field validation on update
  // is a server-side concern handled inline in the handler.
  patch: compensationItemFieldsSchema.partial(),
});

const reorderInput = z.object({
  dealId: z.string(),
  // Item id → new orderIndex. Sparse map is fine; missing items keep
  // their existing index.
  order: z.array(z.object({ id: z.string(), orderIndex: z.number().int().nonnegative() })),
});

/**
 * Recompute and persist the cached `Deal.amount` from the sum of items'
 * cash-equivalent values. Called from within the same transaction as
 * every write so the cache and items are never out of sync.
 *
 * Royalty rows: prefer `expectedVolumeLow * (royaltyPercent/100) *
 * amount`-as-unit-rate when those fields are populated; otherwise fall
 * back to `amount` as a flat number the athlete typed. This matches the
 * "use the conservative low estimate for $600 threshold" rule from
 * US-029.3.
 */
async function recomputeDealAmount(
  // biome-ignore lint/suspicious/noExplicitAny: prisma tx type is verbose
  tx: any,
  dealId: string,
): Promise<void> {
  const items = (await tx.compensationItem.findMany({
    where: { dealId },
    select: {
      type: true,
      amount: true,
      royaltyPercent: true,
      expectedVolumeLow: true,
    },
  })) as Array<{
    type: 'CASH' | 'GOODS_SERVICES' | 'BONUS' | 'ROYALTY';
    amount: { toNumber(): number } | number;
    royaltyPercent: { toNumber(): number } | number | null;
    expectedVolumeLow: { toNumber(): number } | number | null;
  }>;

  const toNum = (v: { toNumber(): number } | number | null): number =>
    v === null || v === undefined
      ? 0
      : typeof v === 'number'
        ? v
        : typeof v?.toNumber === 'function'
          ? v.toNumber()
          : 0;

  const total = items.reduce((sum, it) => {
    if (it.type === 'ROYALTY') {
      const pct = toNum(it.royaltyPercent);
      const vol = toNum(it.expectedVolumeLow);
      const unitRate = toNum(it.amount);
      // If the athlete recorded a low-volume estimate AND a royalty %,
      // use the conservative `unitRate * pct/100 * volumeLow` figure.
      // Otherwise fall back to `amount` (interpreted as a flat estimate).
      if (vol > 0 && pct > 0) {
        return sum + unitRate * (pct / 100) * vol;
      }
    }
    return sum + toNum(it.amount);
  }, 0);

  await tx.deal.update({ where: { id: dealId }, data: { amount: total } });
}

/**
 * Verify the signed-in athlete owns the deal. Returns the deal row when
 * authorized; throws otherwise.
 */
async function requireOwnDeal(
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type is verbose
  db: any,
  userId: string,
  dealId: string,
): Promise<void> {
  const deal = await db.deal.findUnique({
    where: { id: dealId },
    select: { athleteId: true },
  });
  if (!deal) {
    throw new ORPCError('NOT_FOUND', { message: 'Deal not found' });
  }
  if (deal.athleteId !== userId) {
    throw new ORPCError('FORBIDDEN', { message: 'Not your deal' });
  }
}

export const compensationRouter = {
  /**
   * Add a new item to a deal. Sets `orderIndex` to the next-highest
   * value so the new row lands at the end of the list by default.
   */
  addItem: protectedProcedure.input(addItemInput).handler(async ({ context, input }) => {
    const { db, session } = context;
    const userId = session.user.id;
    await requireOwnDeal(db, userId, input.dealId);

    const { createId } = await import('@app/db');

    try {
      // biome-ignore lint/suspicious/noExplicitAny: Prisma.TransactionClient type isn't easily importable here
      return await db.$transaction(async (tx: any) => {
        const last = await tx.compensationItem.findFirst({
          where: { dealId: input.dealId },
          orderBy: { orderIndex: 'desc' },
          select: { orderIndex: true },
        });
        const nextOrder = (last?.orderIndex ?? -1) + 1;

        const row = await tx.compensationItem.create({
          data: {
            id: createId(),
            dealId: input.dealId,
            type: input.item.type,
            amount: input.item.amount,
            currency: input.item.currency ?? 'USD',
            description: input.item.description,
            unitLabel: input.item.unitLabel ?? null,
            royaltyPercent: input.item.royaltyPercent ?? null,
            expectedVolumeLow: input.item.expectedVolumeLow ?? null,
            expectedVolumeHigh: input.item.expectedVolumeHigh ?? null,
            dueDate: input.item.dueDate ? new Date(input.item.dueDate) : null,
            orderIndex: nextOrder,
          },
        });

        await recomputeDealAmount(tx, input.dealId);
        return row;
      });
    } catch (err) {
      if (err instanceof ORPCError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `addItem failed: ${message}` });
    }
  }),

  updateItem: protectedProcedure.input(updateItemInput).handler(async ({ context, input }) => {
    const { db, session } = context;
    const userId = session.user.id;

    const existing = await db.compensationItem.findUnique({
      where: { id: input.id },
      select: { dealId: true },
    });
    if (!existing) throw new ORPCError('NOT_FOUND', { message: 'Item not found' });
    await requireOwnDeal(db, userId, existing.dealId);

    try {
      // biome-ignore lint/suspicious/noExplicitAny: Prisma.TransactionClient type isn't easily importable here
      return await db.$transaction(async (tx: any) => {
        const patch: Record<string, unknown> = {};
        const p = input.patch;
        if (p.type !== undefined) patch.type = p.type;
        if (p.amount !== undefined) patch.amount = p.amount;
        if (p.currency !== undefined) patch.currency = p.currency;
        if (p.description !== undefined) patch.description = p.description;
        if (p.unitLabel !== undefined) patch.unitLabel = p.unitLabel || null;
        if (p.royaltyPercent !== undefined) patch.royaltyPercent = p.royaltyPercent;
        if (p.expectedVolumeLow !== undefined) patch.expectedVolumeLow = p.expectedVolumeLow;
        if (p.expectedVolumeHigh !== undefined) patch.expectedVolumeHigh = p.expectedVolumeHigh;
        if (p.dueDate !== undefined) {
          patch.dueDate = p.dueDate ? new Date(p.dueDate) : null;
        }
        if (p.orderIndex !== undefined) patch.orderIndex = p.orderIndex;

        const row = await tx.compensationItem.update({
          where: { id: input.id },
          data: patch,
        });
        await recomputeDealAmount(tx, existing.dealId);
        return row;
      });
    } catch (err) {
      if (err instanceof ORPCError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `updateItem failed: ${message}` });
    }
  }),

  /**
   * Remove an item. Leaves the deal with zero items if it was the last;
   * the create/update endpoints will not allow saving a deal in that
   * state going forward, but historical deals can still be cleared down
   * for cleanup.
   */
  removeItem: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const userId = session.user.id;

      const existing = await db.compensationItem.findUnique({
        where: { id: input.id },
        select: { dealId: true },
      });
      if (!existing) throw new ORPCError('NOT_FOUND', { message: 'Item not found' });
      await requireOwnDeal(db, userId, existing.dealId);

      try {
        // biome-ignore lint/suspicious/noExplicitAny: Prisma.TransactionClient type isn't easily importable here
        await db.$transaction(async (tx: any) => {
          await tx.compensationItem.delete({ where: { id: input.id } });
          await recomputeDealAmount(tx, existing.dealId);
        });
        return { ok: true };
      } catch (err) {
        if (err instanceof ORPCError) throw err;
        const message = err instanceof Error ? err.message : String(err);
        throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `removeItem failed: ${message}` });
      }
    }),

  /**
   * Bulk-update `orderIndex` across items on a deal. Used by the
   * compensation editor when the athlete reorders rows. Items not
   * mentioned in `order` keep their current index. Does not change
   * `amount` or other fields, so no recomputation needed.
   */
  reorderItems: protectedProcedure.input(reorderInput).handler(async ({ context, input }) => {
    const { db, session } = context;
    const userId = session.user.id;
    await requireOwnDeal(db, userId, input.dealId);

    try {
      // biome-ignore lint/suspicious/noExplicitAny: Prisma.TransactionClient type isn't easily importable here
      await db.$transaction(async (tx: any) => {
        for (const r of input.order) {
          // Each item must belong to the deal — validated implicitly by
          // the where clause; mismatched items become no-ops.
          await tx.compensationItem.updateMany({
            where: { id: r.id, dealId: input.dealId },
            data: { orderIndex: r.orderIndex },
          });
        }
      });
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `reorderItems failed: ${message}` });
    }
  }),
};

// Export the type enum at the router boundary so the deal router can
// pin its create-time items[] to the same shape.
export { compensationTypeEnum };
